import { BarChart, Bar, Cell, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid, ReferenceLine, LabelList } from 'recharts'
import { compact, tickLabel, ChartFigure } from './charts'
import { usePrefersReducedMotion } from '../hooks'

// House-signed amounts: green when the house is up, red when it is down.
const gainColor = (value) => (value < 0 ? 'var(--bb-loss)' : 'var(--bb-gain)')
// always two decimals (formatNumberWithSpaces drops trailing zeros: -1.90 would read -1.9), same separators as the rest of the admin
export const money = (value) => {
  const number = Number(value) || 0
  const [units, cents] = Math.abs(number).toFixed(2).split('.')
  return `${number < 0 && Number(cents) + Number(units) > 0 ? '-' : ''}${units.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}.${cents} €`
}
export const signedMoney = (value) => `${Number(value) > 0 ? '+' : ''}${money(value)}`
const hasData = (rows, keys) => rows?.some((row) => keys.some((key) => row[key]))

// value just right of each bar (for a loss, right of the zero line, which that row leaves empty): never over the instrument names
const BarEndLabel = ({ x, y, width, height, value }) => (
  <text x={Math.max(x, x + width) + 6} y={y + height / 2} dy="0.35em" className='b_book_bar_label'>{signedMoney(value)}</text>
)

// House P&L per period, with the part locked in by pairings beside it and never stacked: it is already inside the P&L.
export const HousePnlChart = ({ data, unit, periodLabels, labels, emptyLabel, summary }) => {
  const still = usePrefersReducedMotion()
  if (!hasData(data, ['house_pnl', 'pairing_result'])) return <p className='b_book_report_empty'>{emptyLabel}</p>
  const tick = tickLabel(unit)

  return (
    <ChartFigure summary={summary}>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" strokeOpacity={0.6} />
          <XAxis dataKey="day" tickFormatter={tick} tickLine={false} minTickGap={16} />
          <YAxis tickFormatter={(value) => compact.format(value)} tickLine={false} width={60} />
          <ReferenceLine y={0} stroke="var(--text-muted)" />
          <Tooltip formatter={(value) => signedMoney(value)} labelFormatter={(day) => `${periodLabels[unit]} ${tick(day)}`} />
          <Legend />
          <Bar isAnimationActive={!still} dataKey="house_pnl" name={labels.house_pnl} fill="var(--bb-gain)">
            {data.map((row) => <Cell key={row.day} fill={gainColor(row.house_pnl)} />)}
          </Bar>
          <Bar isAnimationActive={!still} dataKey="pairing_result" name={labels.pairing_result} fill="var(--text-muted)" />
        </BarChart>
      </ResponsiveContainer>
    </ChartFigure>
  )
}

// Worst instrument on top: the candidates for losing is_b_book.
export const InstrumentPnlChart = ({ rows, label, emptyLabel, summary }) => {
  const still = usePrefersReducedMotion()
  if (!hasData(rows, ['house_pnl'])) return <p className='b_book_report_empty'>{emptyLabel}</p>

  // values printed on the bars: a handful of instruments reads faster than hovering each one, and the sign does not rely on colour
  return (
    <ChartFigure summary={summary}>
      <ResponsiveContainer width="100%" height={60 + rows.length * 40}>
        <BarChart data={rows} layout="vertical" margin={{ left: 10, right: 60 }}>
          <XAxis type="number" hide domain={[(min) => Math.min(0, min * 1.1), (max) => Math.max(0, max * 1.6)]} />
          <YAxis type="category" dataKey="ticker" tickLine={false} width={80} />
          <ReferenceLine x={0} stroke="var(--text-muted)" />
          <Tooltip formatter={(value) => [signedMoney(value), label]} />
          <Bar isAnimationActive={!still} dataKey="house_pnl" name={label} barSize={20}>
            {rows.map((row) => <Cell key={row.ticker} fill={gainColor(row.house_pnl)} />)}
            <LabelList dataKey="house_pnl" content={BarEndLabel} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartFigure>
  )
}

const FEE_SOURCES = [
  { key: 'commissions', color: 'var(--primary)' },
  { key: 'spread', color: 'var(--bb-book-b)' },
  { key: 'overnight', color: 'var(--bb-series-3)' },
]

// The three fee sources stacked: the stack over the range adds up to the total fee revenue tile.
export const FeesSourcesChart = ({ data, unit, periodLabels, labels, emptyLabel, summary }) => {
  const still = usePrefersReducedMotion()
  if (!hasData(data, FEE_SOURCES.map(({ key }) => key))) return <p className='b_book_report_empty'>{emptyLabel}</p>
  const tick = tickLabel(unit)

  return (
    <ChartFigure summary={summary}>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" strokeOpacity={0.6} />
          <XAxis dataKey="day" tickFormatter={tick} tickLine={false} minTickGap={16} />
          <YAxis tickFormatter={(value) => compact.format(value)} tickLine={false} width={60} />
          <Tooltip formatter={(value) => money(value)} labelFormatter={(day) => `${periodLabels[unit]} ${tick(day)}`} />
          <Legend />
          {FEE_SOURCES.map(({ key, color }) => <Bar isAnimationActive={!still} key={key} dataKey={key} name={labels[key]} stackId="fees" fill={color} />)}
        </BarChart>
      </ResponsiveContainer>
    </ChartFigure>
  )
}